/**
 * Client-side correlation ids. These are what stitch the four diagnostic
 * surfaces together (System → Error Logs / Mobile Error Logs / Web Error Logs /
 * Bug Reports); the backend counterpart is crx-be/src/utils/RequestContext.ts.
 *
 *   session id — one per browser tab. Sent as X-Session-Id on every call and
 *                attached to every shipped error log and every bug report, so
 *                one report links to everything else that went wrong in the
 *                same sitting.
 *
 *   request id — one per API call. Sent as X-Request-Id; the server prefixes it
 *                onto every log line it writes while handling that call, so a
 *                failed breadcrumb in a bug report links straight to the server
 *                stack trace behind it.
 */

const SESSION_STORAGE_KEY = "crx.sessionId";

/** Short and log-friendly — full UUIDs make log lines unreadable. */
const newId = (): string => {
    const cryptoRef = typeof crypto !== "undefined" ? (crypto as any) : undefined;
    if (cryptoRef?.randomUUID) {
        return cryptoRef.randomUUID().replace(/-/g, "").slice(0, 12);
    }
    return (Date.now().toString(36) + Math.random().toString(36).slice(2)).slice(0, 12);
};

/** A fresh id for a single API call. */
export const newRequestId = (): string => newId();

/**
 * The id for this browser tab. Held in sessionStorage, so it survives a reload
 * (the user is still in the same sitting) but a new tab is a new session.
 * @returns The tab's session id.
 */
export const getClientSessionId = (): string => {
    try {
        const existing = sessionStorage.getItem(SESSION_STORAGE_KEY);
        if (existing) return existing;

        const created = newId();
        sessionStorage.setItem(SESSION_STORAGE_KEY, created);
        return created;
    } catch {
        // Private mode / storage disabled — correlation degrades to per-call
        // request ids, which is still better than nothing.
        return "nosession";
    }
};
